import React, { useMemo, useState } from 'react'
import { IconButton, makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import CloseIcon from '@material-ui/icons/Close'
import { getPainterAccountAccess } from '@paintscout/util/users'
import { getQuoteOptions } from '@paintscout/util/builder'
import { useClientOptions } from '@paintscout/ui-business'
import type { PainterScheduleEvent } from './PainterScheduleView'
import WorkOrderContent from './PainterScheduleView/WorkOrderContent'
import { usePainterAdditionalWorkStyles } from './PainterScheduleView/usePainterAdditionalWorkStyles'
import { usePainterWorkOrderStyles } from './PainterScheduleView/usePainterWorkOrderStyles'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.palette.background.default
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default
  }
}))

interface PainterWorkOrderPanelProps {
  event: PainterScheduleEvent
  permissions?: string[]
  title?: string
  onClose: () => void
  onAddAdditionalWork?: (eventId: string) => void
  onAcceptSign?: (additionalWorkId: string) => void
  onEditAdditionalWork?: (additionalWorkId: string) => void
  onDeleteAdditionalWork?: (additionalWorkId: string) => void
  onViewMedia?: (additionalWorkId: string) => void
}

export default function PainterWorkOrderPanel({
  event,
  permissions = [],
  title = 'Work Order',
  onClose,
  onAddAdditionalWork,
  onAcceptSign,
  onEditAdditionalWork,
  onDeleteAdditionalWork,
  onViewMedia
}: PainterWorkOrderPanelProps) {
  const classes = useStyles()
  const workClasses = usePainterWorkOrderStyles()
  const additionalWorkClasses = usePainterAdditionalWorkStyles()
  const { options } = useClientOptions()
  const painterAccess = useMemo(() => getPainterAccountAccess(permissions), [permissions])
  const [crewNoteExpanded, setCrewNoteExpanded] = useState(false)
  const [productsExpanded, setProductsExpanded] = useState(false)
  const showHours = useMemo(() => {
    if (!event.sourceQuote) return true
    return getQuoteOptions({ quote: event.sourceQuote, options })?.showWorkOrderAreaValues ?? true
  }, [event.sourceQuote, options])

  const visibleAdditionalWork = painterAccess.canAddAdditionalWork
    ? event.additionalWork ?? []
    : (event.additionalWork ?? []).filter((item) => item.status === 'approved')
  const pricedAdditionalWork = painterAccess.canViewPrices
    ? visibleAdditionalWork
    : visibleAdditionalWork.map((item) => ({ ...item, price: undefined }))

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h3">{title}</Typography>
        <IconButton size="small" onClick={onClose} aria-label="close work order">
          <CloseIcon />
        </IconButton>
      </div>
      <div className={classes.content}>
        <WorkOrderContent
          event={event}
          showContactInfo={painterAccess.canViewClientInfo}
          showHours={showHours}
          canAddAdditionalWork={painterAccess.canAddAdditionalWork && !!event.sourceQuote}
          crewNoteExpanded={crewNoteExpanded}
          productsExpanded={productsExpanded}
          visibleAdditionalWork={pricedAdditionalWork}
          onToggleCrewNote={() => setCrewNoteExpanded((expanded) => !expanded)}
          onToggleProducts={() => setProductsExpanded((expanded) => !expanded)}
          onAddAdditionalWork={() => onAddAdditionalWork?.(event.id)}
          workClasses={workClasses}
          additionalWorkClasses={additionalWorkClasses}
          callbacks={{
            onAcceptSign,
            onEdit: onEditAdditionalWork,
            onDelete: onDeleteAdditionalWork,
            onViewMedia
          }}
        />
      </div>
    </div>
  )
}
